import i18n from '@/i18n';
import { ref } from 'vue';
import moment from 'moment';

const $t = (text) => {
  return i18n.global.t(text);
};

const INTERVAL_LIST = {
  '1m': 60 * 1000,
  '5m': 5 * 60 * 1000,
  '10m': 10 * 60 * 1000,
  '30m': 30 * 60 * 1000,
  '1h': 60 * 60 * 1000
};

export function useChart() {
  const chartGraphic = ref([
    {
      type: 'image',
      top: '20%',
      left: 'center',
      style: {
        image: require('@/assets/img/scmp/no_data.png')
      },
      z: 10
    },
    {
      type: 'text',
      top: '50%',
      left: '44%',
      style: {
        text: $t('label.noData'),
        fill: '#aaa',
        fontFamily: '"Noto Sans KR", Arial, sans-serif',
        fontSize: 14,
        fontWeight: 400,
        color: '#737a8c'
      },
      z: 5
    }
  ]);

  const resetChartGraphic = (chartRef) => {
    chartRef.setOption(
      {
        graphic: [],
        xAxis: {
          show: true
        },
        yAxis: {
          show: true
        }
      },
      {
        replaceMerge: ['graphic']
      }
    );
  };
  // set graphic if chart have at least legend that has data
  const setGraphic = (chartRef, series) => {
    const count = series.map((se) => se.data).flat();
    if (!count.length) {
      chartRef.setOption(
        {
          graphic: chartGraphic.value,
          xAxis: {
            show: false
          },
          yAxis: {
            show: false
          }
        },
        {
          replaceMerge: ['graphic']
        }
      );
    }
  };

  const setChartGraphic = (params, chartRef, init = false) => {
    if (!chartRef) return;
    let series = chartRef.option.series;
    resetChartGraphic(chartRef);
    if (init) {
      setGraphic(chartRef, series);
      return;
    }
    series.forEach((s) => {
      if (params && s.name === params.name) {
        setGraphic(chartRef, [s]);
      }
    });
  };
  // Set nothing display if data chart empty
  const setNothingChart = (params, chartRef, customCssGraphicChart) => {
    resetChartGraphic(chartRef);
    if (customCssGraphicChart) {
      chartGraphic.value = customCssGraphicChart;
    }
    if (params.selected && Object.values(params.selected).every((se) => se === false)) {
      chartRef.setOption(
        {
          graphic: chartGraphic.value,
          xAxis: {
            show: false
          },
          yAxis: {
            show: false
          }
        },
        {
          replaceMerge: ['graphic']
        }
      );
    }
  };

  const addMissingIntervals = (data, intervalTS) => {
    let collectData = [];
    for (let i = 0; i < data.length; i++) {
      collectData.push(data[i]);
      if (!data[i + 1]) break;
      const rate = (data[i + 1].ts - data[i].ts) / intervalTS;
      if (rate <= 1) continue;
      collectData.push({ ts: data[i].ts + intervalTS, value: null });
    }
    return collectData;
  };

  const padStartWithNulls = (collectData, startTimeValue, intervalTS) => {
    const startTimeOfCollectValue = collectData[0].ts;
    if (startTimeValue < startTimeOfCollectValue) {
      const rate = (startTimeOfCollectValue - startTimeValue) / intervalTS;
      const firstPartOfCollectData = [];
      firstPartOfCollectData.push({ ts: startTimeValue, value: null });
      if (rate > 1) {
        for (let j = 1; j < rate; j++) {
          firstPartOfCollectData.push({
            ts: startTimeValue + intervalTS * j,
            value: null
          });
        }
      }
      collectData = [...firstPartOfCollectData, ...collectData];
    }
    return collectData;
  };

  const padEndWithNulls = (collectData, endTimeValue, intervalTS) => {
    const endTimeOfCollectValue = collectData[collectData.length - 1].ts;
    if (endTimeValue > endTimeOfCollectValue) {
      const rate = (endTimeValue - endTimeOfCollectValue) / intervalTS;
      const endPartOfCollectData = [];
      if (rate > 1) {
        for (let j = 1; j < rate; j++) {
          endPartOfCollectData.push({
            ts: endTimeOfCollectValue + intervalTS * j,
            value: null
          });
        }
      }
      endPartOfCollectData.push({ ts: endTimeValue, value: null });
      collectData = [...collectData, ...endPartOfCollectData];
    }
    return collectData;
  };

  // Function for append null to data chart
  // The default of interval unit is minutes, if other unit, please convert to pass parameters
  const handleDataChart = (data, interval, startTime, endTime, padStartEnd = true) => {
    if (!Array.isArray(data) || !data.length || !interval) return data;

    const intervalTS = INTERVAL_LIST[interval] || parseInt(interval) * 60 * 1000;

    const startTimeValue = moment(startTime).valueOf();
    const endTimeValue = moment(endTime).valueOf();

    let filterData = data.filter(({ ts }) => ts >= startTimeValue && ts <= endTimeValue);
    if (!filterData.length) return filterData;
    let collectData = addMissingIntervals(filterData, intervalTS);
    if (!padStartEnd) return collectData;
    collectData = padStartWithNulls(collectData, startTimeValue, intervalTS);
    collectData = padEndWithNulls(collectData, endTimeValue, intervalTS);
    return collectData;
  };

  return { chartGraphic, setChartGraphic, resetChartGraphic, setNothingChart, handleDataChart };
}
